import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Users, Clock, CheckCircle2, FileSignature, ClipboardList,
  TrendingUp, ArrowUpRight, Calendar, Mail, ExternalLink
} from "lucide-react";

export default function OverviewTab({ subCategoryData, categoryColors, category, onRefresh }) {
  const members = subCategoryData?.members || [];
  const pendingInvites = subCategoryData?.pending_invites || [];
  const subCategory = subCategoryData?.sub_category || {};
  
  // Calculate journey stage stats
  const journeyStats = {
    invited: pendingInvites.length,
    accepted: members.filter(m => !m.nda_signed && !m.intake_completed).length,
    ndaSigned: members.filter(m => m.nda_signed && !m.intake_completed).length,
    intakeComplete: members.filter(m => m.intake_completed).length,
    active: members.filter(m => m.status === "active").length
  };

  // Recent activity (last 5 actions)
  const recentActivity = [
    ...members.map(m => ({
      type: m.intake_completed ? "intake" : m.nda_signed ? "nda" : "joined",
      name: m.user_name || m.user_email || "Unknown",
      time: m.updated_at || m.created_at,
      status: m.intake_completed ? "Completed Intake" : m.nda_signed ? "Signed NDA" : "Joined"
    })),
    ...pendingInvites.map(inv => ({
      type: "invited",
      name: inv.name || inv.email || "Unknown",
      time: inv.created_at,
      status: "Invited"
    }))
  ]
    .sort((a, b) => new Date(b.time) - new Date(a.time))
    .slice(0, 5);

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return "Just now";
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    if (diff < 604800000) return `${Math.floor(diff / 86400000)}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="p-6 space-y-6">
      {/* Journey Pipeline */}
      <div>
        <h3 className="text-lg font-semibold mb-4">Journey Pipeline</h3>
        <div className="grid grid-cols-5 gap-2">
          {/* Invited */}
          <div className="relative">
            <Card className="border-amber-200 bg-amber-50">
              <CardContent className="pt-4 pb-4 text-center">
                <div className="w-10 h-10 rounded-full bg-amber-500 text-white flex items-center justify-center mx-auto mb-2">
                  <Mail className="w-5 h-5" />
                </div>
                <p className="text-2xl font-bold text-amber-700">{journeyStats.invited}</p>
                <p className="text-xs text-amber-600">Invited</p>
              </CardContent>
            </Card>
            <div className="absolute top-1/2 -right-1 transform -translate-y-1/2 z-10">
              <ArrowUpRight className="w-4 h-4 text-gray-300 rotate-45" />
            </div>
          </div>
          
          {/* Accepted */}
          <div className="relative">
            <Card className="border-blue-200 bg-blue-50">
              <CardContent className="pt-4 pb-4 text-center">
                <div className="w-10 h-10 rounded-full bg-blue-500 text-white flex items-center justify-center mx-auto mb-2">
                  <CheckCircle2 className="w-5 h-5" />
                </div>
                <p className="text-2xl font-bold text-blue-700">{journeyStats.accepted}</p>
                <p className="text-xs text-blue-600">Accepted</p>
              </CardContent>
            </Card>
            <div className="absolute top-1/2 -right-1 transform -translate-y-1/2 z-10">
              <ArrowUpRight className="w-4 h-4 text-gray-300 rotate-45" />
            </div>
          </div>
          
          {/* NDA Signed */}
          <div className="relative">
            <Card className="border-purple-200 bg-purple-50">
              <CardContent className="pt-4 pb-4 text-center">
                <div className="w-10 h-10 rounded-full bg-purple-500 text-white flex items-center justify-center mx-auto mb-2">
                  <FileSignature className="w-5 h-5" />
                </div>
                <p className="text-2xl font-bold text-purple-700">{journeyStats.ndaSigned}</p>
                <p className="text-xs text-purple-600">NDA Signed</p>
              </CardContent>
            </Card>
            <div className="absolute top-1/2 -right-1 transform -translate-y-1/2 z-10">
              <ArrowUpRight className="w-4 h-4 text-gray-300 rotate-45" />
            </div>
          </div>
          
          {/* Intake Complete */}
          <div className="relative">
            <Card className="border-green-200 bg-green-50">
              <CardContent className="pt-4 pb-4 text-center">
                <div className="w-10 h-10 rounded-full bg-green-500 text-white flex items-center justify-center mx-auto mb-2">
                  <ClipboardList className="w-5 h-5" />
                </div>
                <p className="text-2xl font-bold text-green-700">{journeyStats.intakeComplete}</p>
                <p className="text-xs text-green-600">Intake Done</p>
              </CardContent>
            </Card>
            <div className="absolute top-1/2 -right-1 transform -translate-y-1/2 z-10">
              <ArrowUpRight className="w-4 h-4 text-gray-300 rotate-45" />
            </div>
          </div>
          
          {/* Active */}
          <div>
            <Card className={`${categoryColors.border} ${categoryColors.light}`}>
              <CardContent className="pt-4 pb-4 text-center">
                <div className={`w-10 h-10 rounded-full ${categoryColors.bg} text-white flex items-center justify-center mx-auto mb-2`}>
                  <TrendingUp className="w-5 h-5" />
                </div>
                <p className={`text-2xl font-bold ${categoryColors.text}`}>{journeyStats.active}</p>
                <p className={`text-xs ${categoryColors.text}`}>Active</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Two Column Layout - Recent Activity Full Width */}
      <div className="grid gap-6">
        {/* Recent Activity - Full Width, Clickable */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <Clock className="w-4 h-4" />
              Recent Activity
            </CardTitle>
          </CardHeader>
          <CardContent>
            {recentActivity.length === 0 ? (
              <p className="text-gray-500 text-sm text-center py-4">No recent activity</p>
            ) : (
              <div className="space-y-3">
                {recentActivity.map((activity, idx) => (
                  <div 
                    key={idx} 
                    className="flex items-center justify-between py-2 border-b last:border-0 cursor-pointer hover:bg-gray-50 rounded-lg px-2 -mx-2 transition-colors"
                    onClick={() => {
                      // Navigate to the activity detail
                      if (activity.user_id) {
                        // Navigate to user profile or intake detail
                        window.location.href = `/admin/people?user=${activity.user_id}`;
                      }
                    }}
                    data-testid={`activity-item-${idx}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-medium ${
                        activity.type === "intake" ? "bg-green-500" :
                        activity.type === "nda" ? "bg-purple-500" :
                        activity.type === "joined" ? "bg-blue-500" : "bg-amber-500"
                      }`}>
                        {activity.name.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <p className="text-sm font-medium truncate max-w-[150px]">{activity.name}</p>
                        <p className="text-xs text-gray-500">{activity.status}</p>
                      </div>
                    </div>
                    <span className="text-xs text-gray-400">{formatDate(activity.time)}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions REMOVED - Activity now syncs to main Analytics dashboard */}

      {/* Portal Info */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold">Portal Information</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wide">Name</p>
              <p className="font-medium">{subCategory.name || "N/A"}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wide">Created</p>
              <p className="font-medium">{formatDate(subCategory.created_at)}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wide">Status</p>
              <Badge variant="secondary" className="bg-green-100 text-green-700">Active</Badge>
            </div>
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wide">Description</p>
              <p className="font-medium truncate">{subCategory.description || "No description"}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
